import type { IDTRole } from '@seen/core/models/care-teams/constants'
import { Button, type ButtonProps, cn } from '@seen/ui'
import { cva, type VariantProps } from 'class-variance-authority'
import { forwardRef } from 'react'
import {
  Combobox,
  type SuggestedOption,
} from '#src/components/core/combobox/Combobox.tsx'
import { PractitionerNameplate } from '#src/components/nameplates/PractitionerNameplate.tsx'
import { UserPlaceholderNameplate } from '#src/components/nameplates/UserPlaceholderNameplate.tsx'
import { useOwnerComboboxOptions } from '#src/components/UseOwnerComboboxOptions.tsx'

interface OwnerComboboxButtonProps
  extends
    Omit<ButtonProps, 'value' | 'onChange' | 'size'>,
    VariantProps<typeof variants> {
  value: string | null
  onChange: (value: string | null) => any
  idtRoles?: IDTRole[]
  suggestedOptions?: SuggestedOption<string | null>[]
  suggestedLabel?: string
}

export const OwnerComboboxButton = forwardRef<
  HTMLButtonElement,
  OwnerComboboxButtonProps
>(
  (
    {
      value,
      onChange,
      className,
      size,
      idtRoles,
      suggestedOptions,
      suggestedLabel,
      ...rest
    },
    ref
  ) => {
    const options = useOwnerComboboxOptions(value, idtRoles)
    return (
      <Combobox
        options={options}
        onSelect={onChange}
        suggestedOptions={suggestedOptions}
        suggestedLabel={suggestedLabel}
      >
        <Button
          variant="outline"
          className={cn(variants({ size, className }))}
          ref={ref}
          size={size}
          {...rest}
        >
          {value && (
            <PractitionerNameplate
              practitioner={value}
              size={size === 'xs' ? 3 : 5}
            />
          )}
          {!value && (
            <UserPlaceholderNameplate size={size}>
              Owner
            </UserPlaceholderNameplate>
          )}
        </Button>
      </Combobox>
    )
  }
)

OwnerComboboxButton.displayName = 'OwnerComboboxButton'

const variants = cva('flex items-center shadow-sm', {
  variants: {
    size: {
      default: 'gap-2',
      xs: 'gap-1',
    },
  },
  defaultVariants: {
    size: 'default',
  },
})
